import { useCallback } from 'react';
import { useAppSelector } from 'src/utils/Hooks';
import { useBrandsFollowingIds, useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { useBeaconFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';

/**
 * A hook to provide market share request builders for Doris metrics
 * Adapts the entity filters for market share calculations !!
 */
const useDorisMarketShareHooks = () => {
  const mainEntityType = useAppSelector((state) => state?.entityService?.mainEntity?.type);
  const mainEntity = useAppSelector((state) => state?.entityService?.mainEntity);
  const brandIds = useBrandsFollowingIds();
  const { productEntity } = useProductEntity();
  const { hasActiveFilters, appFilters } = useBeaconFilters();
  const { savedSearchesById } = useAppSelector((state) => state.segments);
  const atlasSegmentGroups = useAppSelector((state) => (state as any).filters?.atlasSegmentGroups as string[][] | null);

  /**
   * Transforms params for a normal request to include appropriate filters
   * DO NOT use this for METRIC COLUMN CHART, IT WILL BE USING DIFFERENT LOGIC, since we require fetching comparison based on the current response record.
   * @param params The base parameters for the request
   * @param isComparison Whether this is for a comparison request
   * @returns Modified parameters for the request
   */
  const requestBuilder = useCallback(
    (params: any) => {
      const modifiedParams = { ...params };

      if (
        mainEntityType === 'category' ||
        mainEntityType === 'subcategory' ||
        mainEntityType === 'businessunit' ||
        mainEntityType === 'segment' ||
        mainEntityType === 'searchtermlist'
      ) {
        modifiedParams.brandIds = brandIds;
      }

      if (mainEntityType === 'brand') {
        modifiedParams.categoryIds = mainEntity?.categoryIds || [];
      }

      // Atlas: prefer pre-grouped segmentIds from Redux if present
      if (atlasSegmentGroups && Array.isArray(atlasSegmentGroups) && atlasSegmentGroups.length > 0) {
        modifiedParams.segmentIds = atlasSegmentGroups as string[][];
      }

      if (hasActiveFilters) {
        if (appFilters.categoryFilters) {
          modifiedParams.categoryIds = appFilters.categoryFilters as number[];
        }

        if (appFilters.subcategoryFilters) {
          modifiedParams.subcategoryIds = appFilters.subcategoryFilters as number[];
        }

        if (!atlasSegmentGroups && appFilters.segmentFilters && !appFilters.businessUnitFilters) {
          // Only add the segment filter if we don't have businessUnitFilters
          // (businessUnitFilters will handle all segments)
          modifiedParams.segmentIds = modifiedParams.segmentIds || [];
          modifiedParams.segmentIds.push([appFilters.segmentFilters.id]);
        }

        // Apply retailPrice filters if available
        if (appFilters.retailPriceFilter) {
          modifiedParams.minRetailPrice = Number(appFilters.retailPriceFilter.minValue) || undefined;
          modifiedParams.maxRetailPrice = Number(appFilters.retailPriceFilter.maxValue) || undefined;
        }

        // This is weird logic, but basically for doris we would want just pass Id instead of the query itself like we used to do in AdvancedSearch,
        // So we will specify the businessUnitIds and segmentIds that we want to use for the request
        if (!atlasSegmentGroups && appFilters.businessUnitFilters) {
          const businessUnitIds = modifiedParams.businessunitids ? [...modifiedParams.businessunitids] : [];

          // Preserve entity segment if viewing a segment entity
          const entitySegmentId =
            mainEntityType === 'segment' || mainEntityType === 'searchtermlist' ? (mainEntity?.id as string) : null;

          // Create a map to track segments by their parent BU
          const segmentsByParentBU = new Map<string, string[]>();

          // Helper function to find parent BU of a segment
          const findParentBU = (segmentId: string): string | null => {
            for (const [id, entity] of savedSearchesById.entries()) {
              if (entity?.type === 'businessunit' && entity.children?.includes(segmentId)) {
                return id;
              }
            }
            return null;
          };

          // Process all items in businessUnitFilters
          appFilters.businessUnitFilters.forEach((businessUnit) => {
            const entity = savedSearchesById.get(businessUnit);

            if (entity?.type === 'segment') {
              // Find which BU this segment belongs to
              const parentBU = findParentBU(businessUnit);

              if (parentBU) {
                // Use parent BU as the grouping key
                if (!segmentsByParentBU.has(parentBU)) {
                  segmentsByParentBU.set(parentBU, []);
                }
                segmentsByParentBU.get(parentBU)!.push(businessUnit);
              } else {
                // No parent BU found, use the segment ID itself as key to keep it separate
                segmentsByParentBU.set(`orphan_${businessUnit}`, [businessUnit]);
              }
            } else if (entity?.type === 'businessunit') {
              // Instead of adding the BU to businessUnitIds, add all its children segments
              const children = entity.children || [];

              // Find the parent of this business unit to group its children correctly
              const parentOfThisBU = findParentBU(businessUnit);
              const groupKey = parentOfThisBU || businessUnit; // Use parent if exists, otherwise use the BU itself

              // Add all child segments to the appropriate group
              children.forEach((childId) => {
                const childEntity = savedSearchesById.get(childId);
                if (childEntity?.type === 'segment') {
                  if (!segmentsByParentBU.has(groupKey)) {
                    segmentsByParentBU.set(groupKey, []);
                  }
                  segmentsByParentBU.get(groupKey)!.push(childId);
                }
              });
            }
          });

          modifiedParams.businessunitids = businessUnitIds;

          const newSegmentIds: string[][] = [];

          if (entitySegmentId) {
            newSegmentIds.push([entitySegmentId]);
          }

          // Add all segment groups
          for (const segmentGroup of segmentsByParentBU.values()) {
            if (segmentGroup.length > 0) {
              newSegmentIds.push(segmentGroup);
            }
          }

          modifiedParams.segmentIds = newSegmentIds;
        }
      }

      return modifiedParams;
    },
    [
      mainEntityType,
      hasActiveFilters,
      brandIds,
      mainEntity?.categoryIds,
      mainEntity?.id,
      appFilters.categoryFilters,
      appFilters.subcategoryFilters,
      appFilters.segmentFilters,
      appFilters.businessUnitFilters,
      appFilters.retailPriceFilter,
      savedSearchesById,
      atlasSegmentGroups,
      productEntity?.categoryIds
    ]
  );

  /**
   * Transforms params for a market share request by removing brand filters and
   * applying appropriate category filters based on entity type
   * @param params The base parameters for the request
   * @param isComparison Whether this is for a comparison request
   * @returns Modified parameters for the market share request
   */
  const marketShareRequestBuilder = useCallback(
    (params: any) => {
      const modifiedParams = { ...params };

      // Remove brand filters for market share calculation
      modifiedParams.brandIds = [];

      if (mainEntityType === 'product') {
        modifiedParams.stacklineSkus = [];
        modifiedParams.categoryIds =
          productEntity?.categoryIds && productEntity.categoryIds.length > 0 ? productEntity.categoryIds : [];
      }

      if (mainEntityType === 'brand') {
        modifiedParams.categoryIds = mainEntity?.categoryIds || [];
      }

      if (mainEntityType === 'businessunit' || mainEntityType === 'segment') {
        modifiedParams.categoryIds = [];
      }

      if (mainEntityType === 'company') {
        modifiedParams.categoryIds = [];
        modifiedParams.businessunitids = [];
        modifiedParams.segmentIds = [];
      }

      if (modifiedParams.branding) {
        modifiedParams.branding = 'all';
      }

      if (mainEntityType === 'microtrend') {
        modifiedParams.subcategoryIds = [mainEntity?.subcategoryId];
        modifiedParams.trendIds = [];
        modifiedParams.marketShareType = 'true';
      }

      // Atlas: prefer pre-grouped segmentIds from Redux if present
      if (atlasSegmentGroups && Array.isArray(atlasSegmentGroups) && atlasSegmentGroups.length > 0) {
        modifiedParams.segmentIds = atlasSegmentGroups as string[][];
      }

      if (hasActiveFilters) {
        if (appFilters.categoryFilters) {
          modifiedParams.categoryIds = appFilters.categoryFilters as number[];
        }

        if (appFilters.subcategoryFilters) {
          modifiedParams.subcategoryIds = appFilters.subcategoryFilters as number[];
        }

        if (!atlasSegmentGroups && appFilters.segmentFilters && !appFilters.businessUnitFilters) {
          // Only add the segment filter if we don't have businessUnitFilters
          // (businessUnitFilters will handle all segments)
          modifiedParams.segmentIds = modifiedParams.segmentIds || [];
          modifiedParams.segmentIds.push([appFilters.segmentFilters.id]);
        }

        // Apply retailPrice filters if available
        if (appFilters.retailPriceFilter) {
          modifiedParams.minRetailPrice = Number(appFilters.retailPriceFilter.minValue) || undefined;
          modifiedParams.maxRetailPrice = Number(appFilters.retailPriceFilter.maxValue) || undefined;
        }

        // This is weird logic, but basically for doris we would want just pass Id instead of the query itself like we used to do in AdvancedSearch,
        // So we will specify the businessUnitIds and segmentIds that we want to use for the request
        if (!atlasSegmentGroups && appFilters.businessUnitFilters) {
          const businessUnitIds = modifiedParams.businessunitids ? [...modifiedParams.businessunitids] : [];

          // Preserve entity segment if viewing a segment entity
          const entitySegmentId =
            mainEntityType === 'segment' || mainEntityType === 'searchtermlist' ? (mainEntity?.id as string) : null;

          // Create a map to track segments by their parent BU
          const segmentsByParentBU = new Map<string, string[]>();

          // Helper function to find parent BU of a segment
          const findParentBU = (segmentId: string): string | null => {
            for (const [id, entity] of savedSearchesById.entries()) {
              if (entity?.type === 'businessunit' && entity.children?.includes(segmentId)) {
                return id;
              }
            }
            return null;
          };

          // Process all items in businessUnitFilters
          appFilters.businessUnitFilters.forEach((businessUnit) => {
            const entity = savedSearchesById.get(businessUnit);

            if (entity?.type === 'segment') {
              // Find which BU this segment belongs to
              const parentBU = findParentBU(businessUnit);

              if (parentBU) {
                // Use parent BU as the grouping key
                if (!segmentsByParentBU.has(parentBU)) {
                  segmentsByParentBU.set(parentBU, []);
                }
                segmentsByParentBU.get(parentBU)!.push(businessUnit);
              } else {
                // No parent BU found, use the segment ID itself as key to keep it separate
                segmentsByParentBU.set(`orphan_${businessUnit}`, [businessUnit]);
              }
            } else if (entity?.type === 'businessunit') {
              // Instead of adding the BU to businessUnitIds, add all its children segments
              const children = entity.children || [];

              // Find the parent of this business unit to group its children correctly
              const parentOfThisBU = findParentBU(businessUnit);
              const groupKey = parentOfThisBU || businessUnit; // Use parent if exists, otherwise use the BU itself

              // Add all child segments to the appropriate group
              children.forEach((childId) => {
                const childEntity = savedSearchesById.get(childId);
                if (childEntity?.type === 'segment') {
                  if (!segmentsByParentBU.has(groupKey)) {
                    segmentsByParentBU.set(groupKey, []);
                  }
                  segmentsByParentBU.get(groupKey)!.push(childId);
                }
              });
            }
          });

          modifiedParams.businessunitids = businessUnitIds;

          const newSegmentIds: string[][] = [];

          if (entitySegmentId) {
            newSegmentIds.push([entitySegmentId]);
          }

          // Add all segment groups
          for (const segmentGroup of segmentsByParentBU.values()) {
            if (segmentGroup.length > 0) {
              newSegmentIds.push(segmentGroup);
            }
          }

          modifiedParams.segmentIds = newSegmentIds;
        }
      }

      return modifiedParams;
    },
    [
      appFilters.businessUnitFilters,
      appFilters.categoryFilters,
      appFilters.retailPriceFilter,
      appFilters.segmentFilters,
      appFilters.subcategoryFilters,
      hasActiveFilters,
      mainEntity?.categoryId,
      mainEntity?.categoryIds,
      mainEntity?.id,
      mainEntityType,
      productEntity?.categoryIds,
      savedSearchesById,
      atlasSegmentGroups
    ]
  );

  return { requestBuilder, marketShareRequestBuilder };
};

export default useDorisMarketShareHooks;
